import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { Observable, of, Subject, throwError } from "rxjs";
import { environment, ENV_BUILD } from "src/environments/environment";
import { LocalEnum } from "../enums/localstorage";
import { Addon } from "../models/addon.model";
import { ApiError } from "../common/api-error";

export const ESP_LOGIN_DATA = "esp.login.data";
const IDENEDI_USER_ID = "idenedi.user.id";
export const IDENEDI_CODE_STORAGE_KEY = "idenedi.auth.code";
export const AUTH_TYPE_HEADER = "x-auth-type";
const TECHNADOPT_AUTH_SETTINGS = {
  domain: "https://partnersevlsbe.azurewebsites.net",
  refreshToken: "7efab48b-6829-42b3-94ba-3cad166eed4d",
  userId: "eca-app@exceeders.com",
  password: "P@ssw0rd"
};

export enum AuthType {
  None = "none",
  Idenedi = "idenedi",
  Technadopt = "technadopt",
  ESP = "esp",
  SimpleStrata = "simple strata",
  Stemexe = "stemexe",
  OppPro = "oppPro",
  CustomTab = "custom tab",
  Higher = "higher",
  PowerBi = "power bi",
  Intajy = "intajy",
  TabOrder = "tabOrder",
  AppAppearance = "App Appearance",
  EngagementPro = "Engpro",
  Office365 = "",
  ChatBot = "",
  Plannexe = "plannexe"
}

@Injectable({
  providedIn: "root"
})
export class AuthService {
  fakeToken: string;
  espOptions: any;
  ssOptions: any;
  private _addonsData: { [groupId: string]: Addon[] } = {};
  private _legacyTechnadoptAddon: Addon;
  private _groupWiseAddon$: { [groupId: string]: Subject<Addon[]> } = {};

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.espOptions = {
      headers: this.buildAuthHeader(AuthType.ESP)
    };
    this.ssOptions = {
      headers: this.buildAuthHeader(AuthType.SimpleStrata)
    };
  }

  get idenediToken(): string {
    let user: any = localStorage.getItem(LocalEnum.user);
    if (user !== null && user !== undefined){
      user = JSON.parse(user);
    }
    return user ? user.idenedi_AccessToken : '';
  }

  get idenediGroupId(): string {
    let user: any = localStorage.getItem(LocalEnum.user);
    if (user !== null && user !== undefined){
      user = JSON.parse(user);
    }
    return user ? user.linkedGroupId : '';
  }

  isLoggedIn(): boolean {
    return !!this.idenediToken;
  }

  // This method is responsible to get and set all addons that system supports for now Technadopt and Esp is handled
  getAddonsAuth(groupId: string, changeLanguage = true): Observable<Addon[]> {
    if (this._addonsData[groupId]?.length) {
      return of(this._addonsData[groupId]);
    }
    if (this._groupWiseAddon$[groupId]) {
      return this._groupWiseAddon$[groupId];
    }
    this._groupWiseAddon$[groupId] = new Subject<Addon[]>();
    const url = `${environment.apiHost}/api/group/${groupId}/addon`;
    const options = { headers: this.buildAuthHeader(AuthType.Idenedi) };
    // const url = `assets/mock/addon.json`;
    // TODO uncomment before commit
    return this.http.get<any>(url, options).pipe(
      map(addons => {
            const simpleStrataAddons = addons.find(
          a => a.Type.toLowerCase() === AuthType.SimpleStrata
        );
        if(simpleStrataAddons && simpleStrataAddons.PublicProperties) {
          localStorage.setItem("organziationId", simpleStrataAddons.PublicProperties.organziationId)
        }
        if (!this._addonsData[groupId]) {
          this._addonsData[groupId] = [];
        }
        const technadoptAddon = addons.find(
          a => a.Type.toLowerCase() === AuthType.Technadopt
        );
        if (technadoptAddon) {
          this._addonsData[groupId].push({
            Type: AuthType.Technadopt,
            BaseUrl: technadoptAddon.BaseUrl,
            AccessToken: technadoptAddon.AccessToken,
            OrganziationId: technadoptAddon?.PublicProperties?.organziationId,
            HomeTopicId: technadoptAddon?.PublicProperties?.homeTopicId || null,
            technadoptAuthData: null
          });
        }
        const simpleStrataAddon = addons.find(
          a => a.Type.toLowerCase() === AuthType.SimpleStrata
        );
        if (simpleStrataAddon) {
          const simpleStrataAuthItem: Addon = new Addon();
          simpleStrataAuthItem.Type = AuthType.SimpleStrata;
          simpleStrataAuthItem.BaseUrl = simpleStrataAddon.BaseUrl;
          const orgId = simpleStrataAddon.PublicProperties["organziationId"];
          if (orgId) {
            simpleStrataAuthItem.OrganziationId = +orgId;
          }

          this._addonsData[groupId].push(simpleStrataAuthItem);
        }
        const espAddon = addons.find(
          a => a.Type.toLowerCase() === AuthType.ESP
        );
        
        if (espAddon) {
          const espAuthItem: Addon = new Addon();
          espAuthItem.Type = AuthType.ESP;
          espAuthItem.BaseUrl = espAddon.BaseUrl;
          espAuthItem.AccessToken = ""; //espAddon.AccessToken
          const orgId = espAddon.PublicProperties["organziationId"];
          localStorage.setItem("espOrganziationId", orgId);
          if (orgId) {
            espAuthItem.OrganziationId = +orgId;
          }
          espAuthItem.Configuration = espAddon.Configuration;
          const list = espAddon.PublicProperties?.subgroupsAccessList;
          if (list && typeof list === 'object') {
            espAddon.PublicProperties.subgroupsAccessList = list;
          } else if (list && typeof list === 'string') {
            try {
              espAddon.PublicProperties.subgroupsAccessList = JSON.parse(list);
            } catch (e) {
              espAddon.PublicProperties.subgroupsAccessList = [];
            }
          } else {
            espAddon.PublicProperties.subgroupsAccessList = [];
          }
          if (espAddon.PublicProperties["claimOwnershipData"]) {
            espAuthItem.espClaimOwnershipData = JSON.parse(
              espAddon.PublicProperties["claimOwnershipData"]
            );
          }
          espAuthItem.PublicProperties = espAddon?.PublicProperties;
          this._addonsData[groupId].push(espAuthItem);
        }
        const customTabAddon: Addon[] = addons.filter(
          a => a.Type.toLowerCase() === AuthType.CustomTab
        );
        if (customTabAddon) {
          customTabAddon.forEach(element => {
            this._addonsData[groupId].push({ ...element });
          });
        }

        const higherAddon = addons.find(
          a => a.Type.toLowerCase() === AuthType.Higher
        );
        if (higherAddon) {
          const higherAuthItem: Addon = new Addon();
          let teamIds = [];
          higherAuthItem.Type = AuthType.Higher;
          // higherAuthItem.BaseUrl = higherAddon.BaseUrl;
          higherAuthItem.BaseUrl = `${higherAddon.BaseUrl}`;
          higherAuthItem.Domain = `${higherAddon.PublicProperties.domain}`;
          if (higherAddon.PublicProperties["homeTopicId"]) {
            teamIds = higherAddon.PublicProperties["homeTopicId"].split(",");
          }
          higherAuthItem.TeamIds = teamIds;

          const orgId = higherAddon.PublicProperties["organziationId"];
          if (orgId) {
            higherAuthItem.OrganziationId = +orgId;
          }

          this._addonsData[groupId].push(higherAuthItem);
        }

        const powerBiAddon = addons.find(
          a => a.Type.toLowerCase() === AuthType.PowerBi
        );
        if (powerBiAddon) {
          const powerBiAuthItem: Addon = new Addon();
          powerBiAuthItem.Type = AuthType.PowerBi;
          powerBiAuthItem.AccessToken = powerBiAddon.AccessToken;
          powerBiAuthItem.BaseUrl = powerBiAddon.BaseUrl;
          powerBiAuthItem.PublicProperties = powerBiAddon.PublicProperties;
          const orgId = powerBiAddon.PublicProperties["organziationId"];
          if (orgId) {
            powerBiAuthItem.OrganziationId = +orgId;
          }
          this._addonsData[groupId].push(powerBiAuthItem);
        }

        const tabOrderAddon: Addon = addons.find(
          a => a.Type === AuthType.TabOrder
        );
        if (tabOrderAddon) {
          const tabOrderAuthItem = this.initTabOrderAddon(tabOrderAddon);

          this._addonsData[groupId].push(tabOrderAuthItem);
        }

        const intajyAddon: Addon = addons.find(
          a => a.Type.toLowerCase() === AuthType.Intajy
        );
        if (intajyAddon) {
          this._addonsData[groupId].push({ ...intajyAddon });
        }

        const appAppearanceAddon: Addon = addons.find(
          a => a.Type === AuthType.AppAppearance
        );
        if (appAppearanceAddon) {
          this._addonsData[groupId].push({ ...appAppearanceAddon });
        }

        const engagementPro: Addon = addons.find(
          a => a.Type === AuthType.EngagementPro
        );
        if (engagementPro) {
          this._addonsData[groupId].push({ ...engagementPro });
        }
        return this._addonsData[groupId];
      }),
      tap(() => {
        this._groupWiseAddon$[groupId].next(this._addonsData[groupId]);
        this._groupWiseAddon$[groupId].complete();
        this._groupWiseAddon$[groupId] = new Subject<Addon[]>();
      })
    );
  }

  getPublicGroupAddon(groupId: string, authType: AuthType): Observable<Addon> {
    const url = `${environment.apiHost}/api/group/${groupId}/addon`;
    const options = { headers: this.buildAuthHeader(AuthType.None) };
    // const url = `assets/mock/addon.json`;
    // TODO uncomment before commit
    // const url = `${environment.apiHost}/api/group/${groupId}/addon`;
    return this.http.get<any>(url, options).pipe(
      map(addonsData => {
        const addonData = addonsData.find(
          a => a.Type.toLowerCase() === authType
        );
        if (!addonData) {
          throwError("No addon data find");
        }
        const resultAddon = new Addon();
        resultAddon.Type = authType;
        resultAddon.BaseUrl = addonData.BaseUrl;
        resultAddon.AccessToken = addonData.AccessToken;
        resultAddon.Domain = addonData.PublicProperties?.Domain;
          resultAddon.OrganziationId = addonData.PublicProperties?.OrganziationId;
        return resultAddon;
      })
    );
  }

  createNewAddon(addon: Addon, groupId: string, type: AuthType) {
    const authHeaders = this.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Idenedi-Api-Version", "4")
    };
    const data = {
      BaseUrl: "",
      AccessToken: "",
      RefreshToken: "",
      PrivateConfiguration: {},
      PublicProperties: addon.PublicProperties
    };
    return this.http
      .post<any>(
        `${environment.apiHost}/api/group/${groupId}/addon?type=${type}`,
        data,
        options
      )
      .pipe(
        tap(() => {
          // Remove all and fetch again
          this._addonsData[groupId] = [];
          // this.getAddonsAuth(groupId).subscribe()
        })
      );
  }

  updateAddon(addon: Addon, groupId: string) {
    const authHeaders = this.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Idenedi-Api-Version", "4")
    };

    const data = {
      BaseUrl: "",
      AccessToken: "",
      RefreshToken: "",
      PrivateConfiguration: {},
      PublicProperties: addon.PublicProperties
    };

    return this.http
      .put<any>(
        `${environment.apiHost}/api/group/${groupId}/addon/${addon.InstanceId}`,
        data,
        options
      )
      .pipe(
        tap(() => {
          // Remove all and fetch again
          this._addonsData[groupId] = [];
          // this.getAddonsAuth(groupId).subscribe()
        })
      );
  }

  deleteAddon(groupId: string, addonId: string) {
    const authHeaders = this.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders
    };

    return this.http
      .delete<any>(
        `${environment.apiHost}/api/group/${groupId}/addon/${addonId}`,
        options
      )
      .pipe(
        tap(() => {
          // Remove all and fetch again
          this._addonsData[groupId] = this._addonsData[groupId].filter(
            item => item.Id != addonId
          );
          // this.getAddonsAuth(groupId).subscribe()
        })
      );
  }

  isJSON(str) {
    try {
        return (JSON.parse(str) && !!str);
    } catch (e) {
        return false;
    }
  }

  initTabOrderAddon(tabOrderAddon: Addon): Addon {
    let parsed;
    if(this.isJSON(tabOrderAddon.PublicProperties?.jsonData)) {
      parsed = JSON.parse(tabOrderAddon.PublicProperties?.jsonData);
    } else {
      parsed = tabOrderAddon.PublicProperties?.jsonData;
    }
    if (Array.isArray(parsed.data)) {
      tabOrderAddon.PublicProperties.jsonData = parsed;
      return { ...tabOrderAddon };
    }
    parsed = JSON.parse(parsed.data);
    tabOrderAddon.PublicProperties.jsonData = {
      data: parsed
    };
    return { ...tabOrderAddon };
  }

  ensureTechnadoptAuth(): Observable<Addon> {
    return this.getAddonsAuth(this.idenediGroupId).pipe(
      switchMap(addons => {
        const technadoptAddon = addons.find(
          addon => addon.Type.toLowerCase() === AuthType.Technadopt
        );

        // We are only relogin to technadopt if the token is available but expired
        // otherwise we are not fetching it here because it would be unnecessary to call
        // we only need to that token when user goes to Products page. For that we have Productresolver
        if (technadoptAddon?.technadoptAuthData) {
          if (
            this.isTechnadoptTokenExpired(
              technadoptAddon.technadoptAuthData.expiresIn
            )
          ) {
            // Relogin
            technadoptAddon.technadoptAuthData = null;
            return this.getTechnadoptAuthorizedTokenIfRequired(technadoptAddon);
          }
        }

        return of(technadoptAddon);
      })
    );
  }

  isTechnadoptTokenExpired(expiresIn: number): boolean {
    return Date.now() > expiresIn - 60 * 1000;
  }

  isUserLoggedInThroughTechnadopt(): Observable<boolean> {
    return this.getAddonsAuth(this.idenediGroupId).pipe(
      switchMap(addons => {
        const technadoptAddon = addons.find(
          addon => addon.Type.toLowerCase() === AuthType.Technadopt
        );
        if (technadoptAddon.technadoptAuthData) {
          return of(true);
        }
        return of(false);
      })
    );
  }

  getTechnadoptAuthorizedTokenIfRequired(
    technadoptAddon: Addon
  ): Observable<Addon> {
    if (
      technadoptAddon &&
      (!technadoptAddon.technadoptAuthData ||
        this.isTechnadoptTokenExpired(
          technadoptAddon.technadoptAuthData.expiresIn
        ))
    ) {
      // Relogin
      // technadoptAddon.technadoptAuthData = null //If second condition is met (token is expired) then we remove the technadoptAuthData so that it doesn't keep calling
      return this.getIdenediAuthorizationCodeForClient(
        environment.exceederClientId
      ).pipe(
        switchMap(code => {
          return this.getTechnadoptToken(technadoptAddon.BaseUrl, code);
        }),
        catchError(e => {
          console.info("Technadopt token not available");
          return of(null);
        })
      );
    } else {
      return of(technadoptAddon);
    }
  }

  getLegacyTechnadoptAuth(): Observable<Addon> {
    if (this._legacyTechnadoptAddon) {
      return of(this._legacyTechnadoptAddon);
    }
    const body = new HttpParams({
      fromObject: {
        username: TECHNADOPT_AUTH_SETTINGS.userId,
        password: TECHNADOPT_AUTH_SETTINGS.password,
        grant_type: "password"
      }
    });
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded"
    });
    return this.http
      .post<any>(`${TECHNADOPT_AUTH_SETTINGS.domain}/token`, body.toString(), {
        headers
      })
      .pipe(
        map(({ access_token }) => {
          const addon = this.createAddon(access_token);
          addon.Type = AuthType.Technadopt;
          addon.BaseUrl = TECHNADOPT_AUTH_SETTINGS.domain;
          this._legacyTechnadoptAddon = addon;
          return addon;
        })
      );
  }

  ensureEspToken(): Observable<Addon> {
    return this.getAddonsAuth(this.idenediGroupId).pipe(
      switchMap(addons => {
        const espAddon = addons.find(
          addon => addon.Type.toLowerCase() === AuthType.ESP.toLowerCase()
        );
        if (espAddon) {
          return of(espAddon);
        } else {
          return of(null);
        }
      })
    );
  }

  ensureSimpleStrataToken(): Observable<Addon> {
    // return this.getAddonsAuth(this.idenediGroupIdForActivities).pipe(
    //   switchMap(addons => {
    //     const simpleStrataAddon = addons.find(
    //       addon =>
    //         addon.Type.toLowerCase() === AuthType.SimpleStrata.toLowerCase()
    //     );
    //     if (simpleStrataAddon) {
    //       return of(simpleStrataAddon);
    //     } else {
    //       return of(null);
    //     }
    //   })
    // );
    return of(null);
  }

  getAuthType(request: HttpRequest<any>): AuthType {
    return (request.headers.get(AUTH_TYPE_HEADER) as AuthType) || AuthType.None;
  }

  buildAuthHeader(authType: AuthType, engProUserId?: string): HttpHeaders {
    let headers;
    if (authType == AuthType.SimpleStrata) {
      if (!!engProUserId && engProUserId != null && engProUserId != "") {
        headers = new HttpHeaders({
          [AUTH_TYPE_HEADER]: authType,
          "client-id": "web",
          locale: "en",
          EngProLoggedInUserId: engProUserId
        });
      } else {
        headers = new HttpHeaders({
          [AUTH_TYPE_HEADER]: authType,
          "client-id": "web",
          locale: "en"
        });
      }
    }
    else if(authType == AuthType.Idenedi){
      headers = new HttpHeaders({
        'authorization' : 'Bearer '+ this.idenediToken
      });
    }
    else {
      headers = new HttpHeaders({
        [AUTH_TYPE_HEADER]: authType
      });
    }

    return headers;
  }

  getAuthAddonByType(authType: AuthType): Observable<Addon> {
    switch (authType) {
      // case AuthType.Idenedi:
      //   return this.ensureIdenediToken().pipe(
      //     map(token => {
      //       return this.createAddon(token);
      //     })
      //   );
      case AuthType.Technadopt:
        return this.ensureTechnadoptAuth();
      case AuthType.ESP:
        return this.ensureEspToken().pipe(
          map(addon => {
            return addon;
          })
        );
      case AuthType.SimpleStrata:
        return this.ensureSimpleStrataToken().pipe(
          map(addon => {
            return addon;
          })
        );
      default:
        return of(null);
    }
  }

  getEspToken(groupId: string) {
    if (this._addonsData[groupId]?.length) {
      const addon = this._addonsData[groupId].find(x => x.Type == AuthType.ESP);
      if (addon) {
        return addon.AccessToken;
      }
    }
    return null;
  }

  getIdenediAuthorizationCodeForClient(clientId: string): Observable<string> {
    const url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=${clientId}`;
    const options = {
      headers: this.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<any>(url, options).pipe(
      map(code => {
        return code;
      })
    );
  }

  getTechnadoptToken(baseURL: string, authCode: string): Observable<any> {
    const body = new HttpParams({
      fromObject: {
        code: authCode,
        grant_type: "idenedi"
      }
    });
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded"
    });
    return this.http.post(baseURL + "token", body.toString(), { headers }).pipe(
      switchMap((tokenData: any) => {
        return this.getAuthAddonByType(AuthType.Technadopt).pipe(
          tap(addOn => {
            if (addOn) {
              addOn.technadoptAuthData = {
                accessToken: tokenData.access_token,
                expiresIn: Date.now() + tokenData.expires_in * 1000,
                roles: JSON.parse(tokenData.roles)
              };
            }
          })
        );
      }),
      catchError(e => {
        return throwError(ApiError.fromResponse(e));
      })
    );
  }

  private createAddon(token: string): Addon {
    const addon: Addon = new Addon();
    addon.AccessToken = token;
    return addon;
  }
}
